import {
  Component,
  inject,
  signal,
  computed,
  effect,
  untracked,
  input,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { IconName } from '../../../shared/icons/icon.component';
import { DatePickerComponent, DateRangePickerOutput } from '../../../shared/components/date-picker/date-picker.component';
import { AiHeadlinesComponent } from '../../../shared/components/ai-headlines/ai-headlines.component';
import { ArrangmentCronDatePickerService } from './services/date-picker.service';
import { TrendsComponent } from './components/trends/trends.component';
import { AdminPerformanceComponent } from './components/admin-performance/admin-performance.component';
import { OverviewNewComponent } from './components/overview-new/overview-new.component';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { CommonService } from '../../../core/services/common.service';
import { AiBotService } from '../../../core/services/ai-bot.service';
import { ProductConfigService } from '../../../core/services/product-config.service';
import {
  DashboardPageKey,
  ArrangmentCronSectionId,
} from '../../../core/enums/dashboard-cards.enum';
import { ApiService } from '../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../core/services/toaster.service';
import {
  findCatalogPageId,
  resolveActiveCatalogProductId,
} from '../../../shared/components/dashboard/dashboard-embed.helpers';
import { DashboardLayoutEditorComponent } from '../../../shared/components/dashboard/dashboard-layout-editor.component';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  normalizeLayoutEditorColSpan,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import {
  ARRANGMENT_CRON_DEFAULT_COL_SPAN,
  ARRANGMENT_CRON_TAB_SECTION_IDS,
  ARRANGMENT_CRON_TAB_SECTIONS,
  ArrangmentCronSectionLayoutItem,
} from './arrangment-cron-layout.catalog';
import { LayoutEditorOverlayDirective } from '../../../shared/components/dashboard/layout-editor-overlay.directive';

/** Draft row used by the shared Customize Layout editor. */
interface TabSectionEditorItem extends ArrangmentCronSectionLayoutItem {
  title: string;
  /** Designed default col-span; lower bound for the editor's "decrease" stepper. */
  defaultColSpan?: number | null;
}

@Component({
  selector: 'app-arrangment-cron',
  standalone: true,
  imports: [
    CommonModule,
    DatePickerComponent,
    TrendsComponent,
    AdminPerformanceComponent,
    OverviewNewComponent,
    // AiHeadlinesComponent,
    IconComponentV2,
    DragDropModule,
    // LayoutEditorOverlayDirective,
    // DashboardLayoutEditorComponent,
  ],
  templateUrl: './arrangment-cron.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
  `],
})
export class ArrangmentCronComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(OverviewNewComponent) overviewNew?: OverviewNewComponent;
  @ViewChild(TrendsComponent) trends?: TrendsComponent;
  @ViewChild(AdminPerformanceComponent) adminPerformance?: AdminPerformanceComponent;

  private datePickerService = inject(ArrangmentCronDatePickerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private _commonService = inject(CommonService);
  private aiBotService = inject(AiBotService);
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private toaster = inject(ToasterService);
  private queryParamSubscription: Subscription | null = null;
  private resolveAgentSubscription: Subscription | null = null;
  private isInitialized = false;
  private isInternalNavigation = false;

  // Inputs that mirror the daily-cron pattern so this component can also
  // be embedded in My Dashboard later. They default to standalone so
  // existing routes keep behaving exactly like before.
  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.ARRANGEMENT_CRON_PAYMENTS;
  readonly SectionId = ArrangmentCronSectionId;

  /** Maps button value (1/2/3) to its section id (used by the per-tab
   *  pin button + per-tab popup persistence). */
  private readonly TAB_VALUE_TO_SECTION_ID: Record<number, ArrangmentCronSectionId> = {
    1: ArrangmentCronSectionId.OVERVIEW,
    2: ArrangmentCronSectionId.TRENDS_RECOVERY,
    3: ArrangmentCronSectionId.ADMIN_PERFORMANCE,
  };

  // Map button values to URL-friendly tab names
  private readonly tabMap: Record<number, string> = {
    1: 'overview',
    2: 'trends',
    3: 'admin-performance',
  };

  private readonly reverseTabMap: Record<string, number> = {
    'overview': 1,
    'trends': 2,
    'admin-performance': 3,
  };

  selectionButtons = [
    {
      label: 'Overview',
      value: 1,
      sectionId: ArrangmentCronSectionId.OVERVIEW,
      icon: 'chartLine' as IconName,
      tooltip: 'Comprehensive dashboard view showing key metrics, statistics, and overall arrangement cron performance at a glance of all time data',
      isActive: true,
    },
    {
      label: 'Trends and Recovery',
      value: 2,
      sectionId: ArrangmentCronSectionId.TRENDS_RECOVERY,
      icon: 'trendingUp' as IconName,
      tooltip: 'Analyze historical patterns, growth trends, and performance metrics over time to identify insights and opportunities for recovery and improvement and pending-active arrangements',
      isActive: false,
    },
    {
      label: 'Admin Performance',
      value: 3,
      sectionId: ArrangmentCronSectionId.ADMIN_PERFORMANCE,
      icon: 'user' as IconName,
      tooltip: 'Evaluate individual and team performance metrics, productivity statistics, and efficiency indicators for admin staff',
      isActive: false,
    },
  ];

  selectedButton = signal<number | null>(null);

  // Date picker state
  startDate = this.datePickerService.startDate;
  endDate = this.datePickerService.endDate;
  unit = this.datePickerService.unit;

  // Show date picker only when not Overview tabs (buttons 0, 1)
  showDatePicker = computed(() => {
    const selected = this.selectedButton();
    return selected !== 1;
  });

  showAiButton = computed(() => {
    const selected = this.selectedButton();
    return selected === 1 || selected === 2 || selected === 3;
  });

  isDenefits = computed(() =>
    this.productConfigService.isDenefitsProduct(this.apiService.selectedProductId())
  );

  /** Ask Prism AI (resolve agent, suggestions, panel) is Denefits-only. */
  private canUseAskAi(productId?: string | null): boolean {
    const id = productId ?? this.apiService.getSelectedProductId() ?? sessionStorage.getItem('selected_product_id');
    return this.productConfigService.isDenefitsProduct(id);
  }

  // Get selected button tooltip/description
  selectedButtonTooltip = computed(() => {
    const selected = this.selectedButton();
    const button = this.selectionButtons.find(b => b.value === selected);
    return button?.tooltip || '';
  });

  /** Section id of the currently active tab — used by the popup to
   *  decide which section catalog to render. `null` while the tab
   *  selection is still being resolved from the query params. */
  readonly activeTabSectionId = computed<ArrangmentCronSectionId | null>(() => {
    const v = this.selectedButton();
    return v != null ? this.TAB_VALUE_TO_SECTION_ID[v] ?? null : null;
  });

  /** Friendly label for the active tab — surfaces inside the popup
   *  header so the user always knows which tab they're customizing. */
  readonly activeTabLabel = computed<string>(() => {
    const v = this.selectedButton();
    if (v == null) return '';
    const btn = this.selectionButtons.find((b) => b.value === v);
    return btn?.label ?? '';
  });

  constructor() {
    // Resolve catalog page id for Arrangement Cron Payments.
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    // Hydrate already-pinned tabs so the "Added" badge is accurate
    // after a page refresh. No-op when the catalog hasn't registered the
    // Arrangement Cron Payments page yet.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<ArrangmentCronSectionId>();
          keys.forEach((k) => {
            if (Object.values(ArrangmentCronSectionId).includes(k as ArrangmentCronSectionId)) {
              pinned.add(k as ArrangmentCronSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per tab.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<string, number>();
            for (const tabId of ARRANGMENT_CRON_TAB_SECTION_IDS) {
              for (const sec of ARRANGMENT_CRON_TAB_SECTIONS[tabId]) {
                const id = widgetIdMap.get(sec.id);
                if (id != null) next.set(sec.id, id);
              }
            }
            for (const sec of Object.values(ArrangmentCronSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  ngOnInit(): void {
    this._initAllTabLayouts();

    // Subscribe to query params to handle browser back/forward and reload
    this.queryParamSubscription = this.route.queryParams.subscribe(params => {
      const tabParam = params['tab'] || 'overview';
      const contextKey = `ARRANGMENT_CRON_${tabParam.toUpperCase().replace('-', '_')}`;

      const oldTabValue = this.selectedButton();
      const newTabValue = this.reverseTabMap[tabParam];

      if (newTabValue !== undefined) {
        if (newTabValue !== oldTabValue) {
          this.selectedButton.set(newTabValue);

          const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
          if (this.canUseAskAi(productId)) {
            const productName = this.productConfigService.getProductName(productId!).toLowerCase();
            this.setAiSuggestions();
            this._commonService.activeChatContextKey.set(contextKey);

            if (productName) {
              if (this.isInitialized) {
                this.resolveAiAgent(productName);
              } else if (this._commonService.isPanelOpen()) {
                this.openAiAssistant();
              } else {
                this.resolveAiAgent(productName);
              }
            }
          }
        }
      } else {
        this.selectedButton.set(1);
        if (this.canUseAskAi()) {
          this.setAiSuggestions();
          this._commonService.activeChatContextKey.set('ARRANGMENT_CRON_OVERVIEW');
        }
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: 'overview' },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.isInitialized = true;
    }, 0);
  }

  private setAiSuggestions(): void {
    const tab = this.selectedButton();
    if (tab === 1) {
      this._commonService.aiSuggestions.set([
        'Show overall arrangement cron performance and success rates',
        'Summarize morning, afternoon, and evening payment outcomes',
        'Analyze total scheduled amounts and pending arrangements',
        'List the most recent payment retry attempts and their results'
      ]);
    } else if (tab === 2) {
      this._commonService.aiSuggestions.set([
        'Show arrangement creation and scheduled trends over time',
        'Analyze recovery rates and recovered amounts across different payment types',
        'Compare recurring and partial arrangement success rates',
        'What are the key growth patterns in arrangement scheduling?'
      ]);
    } else if (tab === 3) {
      this._commonService.aiSuggestions.set([
        'Evaluate overall admin staff performance and productivity scores',
        'Compare arrangements created, executed, and success rates by admin',
        'Which admins have the highest recovery rate and recovered amount?',
        'Show details of far-future arrangement alerts and gaps by admin'
      ]);
    } else {
      this._commonService.aiSuggestions.set([
        'Show overall arrangement cron performance and success rates',
        'Summarize morning, afternoon, and evening payment outcomes',
        'Analyze total scheduled amounts and pending arrangements',
        'List the most recent payment retry attempts and their results'
      ]);
    }
  }

  ngOnDestroy(): void {
    this.queryParamSubscription?.unsubscribe();
    this.resolveAgentSubscription?.unsubscribe();
    if (!this.isInternalNavigation) {
      this._commonService.activeAgentId.set(null);
      this._commonService.isPanelOpen.set(false);
      this._commonService.activeChatContextKey.set(null);
    }
  }

  selectTab(value: number): void {
    this.isInternalNavigation = true;
    this.selectedButton.set(value);

    const tabName = this.tabMap[value] || 'overview';
    const contextKey = `ARRANGMENT_CRON_${tabName.toUpperCase().replace('-', '_')}`;

    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (this.canUseAskAi(productId)) {
      this.setAiSuggestions();
      this._commonService.activeChatContextKey.set(contextKey);

      // resolveAiAgent is NOT called from the queryParams subscription on internal
      // navigation because selectedButton is already updated by this point, making
      // oldTabValue === newTabValue and skipping the entire block. Resolve here instead.
      if (productId) {
        const productName = this.productConfigService.getProductName(productId).toLowerCase();
        if (productName) {
          this.resolveAiAgent(productName);
        }
      }
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabName },
      queryParamsHandling: 'merge',
    });

    setTimeout(() => {
      this.isInternalNavigation = false;
    }, 100);
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    this.datePickerService.updateDateRange(event);
  }

  openAiAssistant(): void {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!productId || !this.canUseAskAi(productId)) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    this._commonService.chatType.set('ARRANGMENT_CRON');
    this._commonService.isPanelOpen.set(true);

    const contextKey = this._commonService.activeChatContextKey();
    if (contextKey) {
      const savedState = this._commonService.getChatState(contextKey);
      if (!savedState) {
        this.setAiSuggestions();
      }
    }

    this.resolveAiAgent(productName);
  }

  resolveAiAgent(productName: string): void {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!this.canUseAskAi(productId)) {
      this.resolveAgentSubscription?.unsubscribe();
      this._commonService.activeAgentId.set(null);
      return;
    }

    // Cancel any in-flight request so a stale response from a previous tab
    // cannot overwrite the agentId resolved for the current tab.
    this.resolveAgentSubscription?.unsubscribe();

    let pageKey = 'tab_overview';
    if (this.selectedButton() === 2) {
      pageKey = 'tab_trends';
    } else if (this.selectedButton() === 3) {
      pageKey = 'admin_performance';
    }
    this.resolveAgentSubscription = this.aiBotService.resolveAgent(productName, pageKey, 'ask_prism_ai').subscribe({
      next: (res: any) => {
        const agentId = res?.agent_id || res?.data?.agent_id;
        if (agentId) {
          this._commonService.activeAgentId.set(agentId);
        } else {
          this._commonService.activeAgentId.set(null);
        }
      },
      error: () => {
        this._commonService.activeAgentId.set(null);
      }
    });
  }

  // ─── Per-tab section layouts (visibility + order) ──────────────────────
  /**
   * Layout state per tab. Each entry maps a tab's section id to that
   * section's persisted visibility + order within the tab. Indexed by
   * the tab's `ArrangmentCronSectionId` so the three tabs never share
   * state.
   */
  readonly tabLayouts = signal<
    Record<ArrangmentCronSectionId, Record<string, ArrangmentCronSectionLayoutItem>>
  >(
    {} as Record<ArrangmentCronSectionId, Record<string, ArrangmentCronSectionLayoutItem>>,
  );

  /** Layout map for the active tab — passed to child tab components. */
  readonly activeTabLayoutMap = computed(() => {
    const tabId = this.activeTabSectionId();
    if (!tabId) return {};
    return this.tabLayouts()[tabId] ?? {};
  });

  // ─── "Pin to Custom Dashboard" (per-tab; pins the whole tab) ───────────
  readonly pinnedSections = signal<Set<ArrangmentCronSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<ArrangmentCronSectionId>>(new Set());
  readonly pinnableWidgetIds = signal<Map<string, number>>(new Map());

  private _toSectionId(
    sec: { sectionId?: ArrangmentCronSectionId; id?: ArrangmentCronSectionId } | ArrangmentCronSectionId,
  ): ArrangmentCronSectionId | null {
    if (typeof sec === 'string') return sec;
    if (sec == null) return null;
    return (sec.sectionId ?? sec.id) ?? null;
  }

  isAddedToCustomDashboard(
    sec: { sectionId?: ArrangmentCronSectionId; id?: ArrangmentCronSectionId } | ArrangmentCronSectionId,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pinnedSections().has(id) : false;
  }

  isAddingToCustomDashboard(
    sec: { sectionId?: ArrangmentCronSectionId; id?: ArrangmentCronSectionId } | ArrangmentCronSectionId,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pendingPinSections().has(id) : false;
  }

  canPinToCustomDashboard(
    sec: { sectionId?: ArrangmentCronSectionId; id?: ArrangmentCronSectionId } | ArrangmentCronSectionId,
  ): boolean {
    if (this.mixedMode()) return false;
    return !!this._toSectionId(sec);
  }

  addToCustomDashboard(
    sec: { sectionId?: ArrangmentCronSectionId; id?: ArrangmentCronSectionId } | ArrangmentCronSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toaster?.info?.(
        'Pinning Arrangement Cron tabs to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId as string);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan: 12,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan: 12,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Customize Layout editor (per-tab section editor) ──────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<TabSectionEditorItem[]>([]);

  /** Snapshots which tab the editor was opened against so Apply / reset
   *  always write to the correct tab even if the user navigates away
   *  with the popup still open (the popup itself blocks navigation, but
   *  this also future-proofs the flow). */
  readonly editorTargetTabId = signal<ArrangmentCronSectionId | null>(null);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  private _layoutSnapshot:
    | Record<ArrangmentCronSectionId, Record<string, ArrangmentCronSectionLayoutItem>>
    | null = null;

  /** localStorage key for a given tab's persisted layout. Per-tab so
   *  customizations on one tab never affect another. */
  private _layoutLsKey(tabId: ArrangmentCronSectionId): string {
    return `prism_arrangment_cron_${tabId}_layout_v2`;
  }

  private _sectionCatalogFor(tabId: ArrangmentCronSectionId) {
    return ARRANGMENT_CRON_TAB_SECTIONS[tabId] ?? [];
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    return normalizeLayoutEditorColSpan(span);
  }

  openCustomizeLayout(): void {
    if (this.selectedButton() === 1) {
      this.overviewNew?.openCustomizeLayout();
      return;
    }
    if (this.selectedButton() === 2) {
      this.trends?.openCustomizeLayout();
      return;
    }
    if (this.selectedButton() === 3) {
      this.adminPerformance?.openCustomizeLayout();
      return;
    }

    const tabId = this.activeTabSectionId();
    if (!tabId) return;

    const catalog = this._sectionCatalogFor(tabId);
    const titleById = new Map(catalog.map((s) => [s.id, s.title]));
    const stored = this.tabLayouts()[tabId] ?? {};
    const items: TabSectionEditorItem[] = catalog
      .map((s, idx) => {
        const item = stored[s.id];
        return {
          id: s.id,
          title: s.title,
          visible: item?.visible ?? true,
          colSpan: this._normalizeColSpan(
            item?.colSpan ?? ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
          ),
          order: Number.isFinite(item?.order) ? Number(item!.order) : idx,
        };
      })
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        ...s,
        title: titleById.get(s.id) ?? s.id,
        colSpan: this._normalizeColSpan(s.colSpan),
        defaultColSpan: this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
        ),
      }));

    this._layoutSnapshot = JSON.parse(JSON.stringify(this.tabLayouts()));
    this.editorTargetTabId.set(tabId);
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.tabLayouts.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
    this.editorTargetTabId.set(null);
  }

  toggleEditorCardVisibility(id: string): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    const tabId = this.editorTargetTabId();
    if (!tabId) return;
    const defaults: TabSectionEditorItem[] = this._sectionCatalogFor(tabId).map(
      (s, idx) => {
        const defaultSpan = this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
        );
        return {
          id: s.id,
          title: s.title,
          visible: true,
          colSpan: defaultSpan,
          order: idx,
          defaultColSpan: defaultSpan,
        };
      },
    );
    this.editorItems.set(defaults);
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const tabId = this.editorTargetTabId();
    if (!tabId) {
      this.isLayoutEditorOpen.set(false);
      return;
    }

    const draft = this.editorItems();
    if (!draft.some((d) => d.visible)) {
      this.toaster?.warning?.(
        'At least one section must be visible. Toggle a section on before applying.',
      );
      return;
    }

    const next = this._buildLayoutFromEditor(tabId);
    this.tabLayouts.update((all) => ({ ...all, [tabId]: next }));
    this._saveLayoutToStorage(tabId, next);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(tabId, draft);

    this.toaster?.success?.(
      `Layout updated for ${this._tabLabelFor(tabId)}.`,
    );

    this.isLayoutEditorOpen.set(false);
    this.editorTargetTabId.set(null);
  }

  decreaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[i.id] ?? 12,
        );
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  onEditorCardDrop(event: CdkDragDrop<TabSectionEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, { strategy: 'shift' });
    if (!next) return;
    this.editorItems.set(next);
    this._syncLayoutFromEditor();
  }

  private _buildLayoutFromEditor(
    tabId: ArrangmentCronSectionId,
  ): Record<string, ArrangmentCronSectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this._sectionCatalogFor(tabId),
      ARRANGMENT_CRON_DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    const tabId = this.editorTargetTabId();
    if (!tabId || !this.isLayoutEditorOpen()) return;
    const next = this._buildLayoutFromEditor(tabId);
    this.tabLayouts.update((all) => ({ ...all, [tabId]: next }));
  }

  private _initAllTabLayouts(): void {
    // Overview, Trends, and Admin Performance each own their subsection layout.
    this.tabLayouts.set(
      {} as Record<ArrangmentCronSectionId, Record<string, ArrangmentCronSectionLayoutItem>>,
    );
  }

  private _loadLayoutFromStorage(
    tabId: ArrangmentCronSectionId,
  ): Record<string, ArrangmentCronSectionLayoutItem> | null {
    try {
      const raw =
        localStorage.getItem(this._layoutLsKey(tabId)) ??
        localStorage.getItem(`prism_arrangment_cron_${tabId}_layout_v1`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    tabId: ArrangmentCronSectionId,
    layout: Record<string, ArrangmentCronSectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this._layoutLsKey(tabId), JSON.stringify(layout));
    } catch {
      // localStorage unavailable — silently ignore.
    }
  }

  private _tabLabelFor(tabId: ArrangmentCronSectionId): string {
    const value = Object.entries(this.TAB_VALUE_TO_SECTION_ID)
      .find(([, sid]) => sid === tabId)?.[0];
    if (value == null) return '';
    const btn = this.selectionButtons.find((b) => b.value === Number(value));
    return btn?.label ?? '';
  }

  /**
   * Best-effort backend persistence for per-tab subsection layouts.
   * Mirrors daily-cron / overdue-cron — local layout above is unchanged
   * regardless of network outcome.
   */
  private _persistLayoutToBackend(
    _tabId: ArrangmentCronSectionId,
    draft: ReadonlyArray<TabSectionEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) return;

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }
}
